import { Box, Typography, useTheme } from '@mui/material';
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { ProjectSummaryCards } from './components/ProjectSummaryCards';
import { StatusDot } from './components/StatusIcon';
import { STATUS_ICON_COLOR_MAPPING } from './constants';
import { useAnnotatorProjectsDataGridFetch } from './hook/useDatagridAnnotatorProjectsFetch';
import { setProjectsListPaginationModel } from './state/annotatorProjectSlice';
import { selectProjectsListPage, selectProjectsListPageSize } from './state/selector';
import { AnnotatorProjectResonse } from './types/response';
import { useAnnotatorProjectsScreen } from './useAnnotatorProjectsScreen';
import { DATAGRID_PAGE_SIZE_OPTIONS } from '../../common/constants';
import { DataGridToolbar } from '../../components/DatagridToolbar/DataGridToolbar';
import { getAnnotationToolAnnotatorQueuePath } from '../../lib/routePaths';
import { useAppDispatch, useAppSelector } from '../../lib/store';
import { DataGridLayout } from '../../wrapper-components/DataGrid/components/DataGridLayout/DataGridLayout';
import { DataGrid } from '../../wrapper-components/DataGrid/DataGrid';
import { setProjectId } from '../annotator-queue/state/annotatorQueueSlice';

export const AnnotatorProjectsScreen = () => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const {
    referenceStandardReviewProjectsCount,
    inProgressProjectsCount,
    completedProjectsCount,
    totalProjectsCount,
    isProjectsSummaryLoading,
  } = useAnnotatorProjectsScreen();

  const dispatch = useAppDispatch();
  const projectsListPage = useAppSelector(selectProjectsListPage);
  const projectsListPageSize = useAppSelector(selectProjectsListPageSize);

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    dispatch(setProjectsListPaginationModel({ page: model.page, pageSize: model.pageSize }));
  };

  const { isFetching } = useAnnotatorProjectsDataGridFetch() || {};
  useEffect(() => {
    if (isFetching) {
      dispatch(setProjectsListPaginationModel({ page: 0, pageSize: projectsListPageSize }));
    }
  }, [isFetching]);

  const columns: GridColDef<AnnotatorProjectResonse>[] = useMemo(
    () => [
      {
        field: 'projectName',
        headerName: 'Project name',
        minWidth: 210,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => {
          const { projectName, projectId } = params.row;
          return (
            <Link
              to={getAnnotationToolAnnotatorQueuePath()}
              onClick={() => dispatch(setProjectId(projectId))}
              style={{
                textDecoration: 'none',
                color: theme.palette.primary.main,
                fontWeight: 'bold',
              }}
            >
              {projectName}
            </Link>
          );
        },
      },
      {
        field: 'conditionArea',
        headerName: 'Condition area',
        minWidth: 190,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'healthSystem',
        headerName: 'Research partner',
        minWidth: 180,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 200,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (value: any, row: AnnotatorProjectResonse) => {
          if (!row?.status) return '';
          return formatMessage({
            id: `annotatorProjects.content.table.status.${row.status.toLocaleLowerCase()}`,
          });
        },
        renderCell: (params) => {
          const { status } = params.row;
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing(2),
                justifyContent: 'center',
              }}
            >
              <StatusDot
                color={
                  STATUS_ICON_COLOR_MAPPING(theme)[status as keyof typeof STATUS_ICON_COLOR_MAPPING]
                }
              />
              <Typography variant="body2" color="text.secondary">
                {formatMessage({
                  id: `annotatorProjects.content.table.status.${status.toLocaleLowerCase()}`,
                })}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        minWidth: 180,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (value?: string | null) => {
          return value ? dayjs(value).format('MMM D, YYYY') : '-';
        },
      },
      {
        field: 'startDate',
        headerName: 'Started',
        minWidth: 180,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (value?: string | null) => {
          return value ? dayjs(value).format('MMM D, YYYY') : '-';
        },
      },
      {
        field: 'completedDate',
        headerName: 'Completed',
        minWidth: 180,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (value?: string | null) => {
          return value ? dayjs(value).format('MMM D, YYYY') : '-';
        },
      },
      {
        field: 'encounters',
        minWidth: 250,
        type: 'string',
        align: 'right',
        headerAlign: 'right',
        valueGetter: (value: any, row: AnnotatorProjectResonse) => {
          if (!row) return '';
          return `${row.validationCount} / ${row.trainingCount} / ${row.testCount}`;
        },
        renderHeader: () => (
          <Box sx={{ lineHeight: 1.2, fontWeight: 'bold', textAlign: 'right' }}>
            Encounters
            <br />
            (Validation / Training / Test)
          </Box>
        ),
        renderCell: (params) => {
          const { trainingCount, testCount, validationCount } = params.row;
          return (
            <Box>
              {validationCount} / {trainingCount} / {testCount}
            </Box>
          );
        },
      },
    ],
    [formatMessage, theme]
  );
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: theme.spacing(4),
        }}
      >
        <Typography variant="h4">
          {formatMessage({ id: 'annotatorProjects.header.title' })}
        </Typography>
      </Box>
      <Box>
        <ProjectSummaryCards
          referenceStandardReviewProjectsCount={referenceStandardReviewProjectsCount}
          inProgressProjectsCount={inProgressProjectsCount}
          completedProjectsCount={completedProjectsCount}
          totalProjectsCount={totalProjectsCount}
          isProjectsSummaryLoading={isProjectsSummaryLoading}
        />
      </Box>
      <Box sx={{ p: theme.spacing(4) }}>
        <DataGridLayout id="annotator-projects-data-grid">
          <DataGrid
            id="annotator-projects-data-grid"
            mode="client"
            pagination
            loadOnFetch
            pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
            paginationModel={{ page: projectsListPage, pageSize: projectsListPageSize }}
            onPaginationModelChange={handlePaginationModelChange}
            disableSearchParamSync
            preventUrlReadWhenSyncDisabled
            getRowId={(row) => row.projectId}
            columns={columns}
            useFetchDataGridData={useAnnotatorProjectsDataGridFetch}
            slots={{ toolbar: DataGridToolbar }}
            sx={{
              maxHeight: '70vh',
              '& .MuiDataGrid-row': {
                borderBottom: `1px solid ${theme.palette.divider}`,
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                whiteSpace: 'normal',
                lineHeight: '2rem',
              },
              '& .MuiDataGrid-columnHeader': {
                alignItems: 'flex-start',
              },
              '& .MuiDataGrid-cell--pinnedLeft': {
                borderRight: `2px solid ${theme.palette.divider}`,
              },
              '& .MuiDataGrid-columnHeader--pinnedLeft': {
                borderRight: `2px solid ${theme.palette.divider}`,
              },
            }}
            initialState={{
              pinnedColumns: {
                left: ['projectName'],
              },
            }}
          />
        </DataGridLayout>
      </Box>
    </Box>
  );
};
